import React, { useContext, useEffect, useMemo, useState } from "react";
import mainContext from "../context/MainContext/mainContext";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Input,
  Select,
  notification,
  Modal,
  Image,
  message,
  Upload,
  Tag,
} from "antd";
import { MdDelete } from "react-icons/md";
import { IoArrowBack } from "react-icons/io5";
import SalesOrderForm from "./SalesOrderForm";
import InventoryList from "./InventoryList";
import GrandTotal from "./GrandTotal";
import TotalProductionCost from "./TotalProductionCost";
import { AiOutlineEye, AiOutlineUpload } from "react-icons/ai";
import { FaFileExcel } from "react-icons/fa";
import { Switch } from "antd";
import SuffixInvList from "./SuffixInvList";
import { exportSalesOrderDetailsExcel } from "../utils/exportSalesOrderDetailsExcel";
import convertS3ToCloudFront, {
  openFileInNewTab,
} from "../../../../../utils/convertS3ToCloudFront";
const { Option } = Select;
const { Dragger } = Upload;
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

/** Billable subtotal: per-inventory charges + suffix billing amounts (matches load from BDF). Excludes purchase/maintenance rate. */
function computeBillableSubTotal(order) {
  if (!order) return 0;
  const inventories = order.inventories || [];
  const suffixInventories = order.suffixInventories || [];
  const fromInventories = inventories.reduce(
    (acc, next) =>
      acc +
      (Number(next?.specialRate) || 0) +
      (Number(next?.mountingCharge) || 0) +
      (Number(next?.printingCharge) || 0) +
      (Number(next?.innovationRate) || 0),
    0,
  );
  const fromSuffix = suffixInventories.reduce(
    (acc, next) => acc + (Number(next?.specialRate) || 0),
    0,
  );
  return fromInventories + fromSuffix;
}

const SalesOrderDetails = () => {
  //states
  const [fileUrl, setFileUrl] = useState(null); // State to store the uploaded file URL
  const { id, justView } = useContext(mainContext);
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isPreviosYear, setIsPreviosYear] = useState(false);
  const [previousYear, setPreviousYear] = useState(null);
  const [grandTotal, setGrandTotal] = useState(0);
  const [ROModal, setROModal] = useState(false);
  const [switchGst, setSwitchGst] = useState(false);
  const [log, setLog] = useState(null);
  const [salesOrderData, setSalesOrderData] = useState({
    gstNumber: null,
    bdfType: null,
    stamp: null,
    roNumber: null,
    parentCompanyName: null,
    remark: null,
    roFile: null,
    brandName: null,
    motherBrand: null,
    brandId: null,
    brandSegment: null,
    taxType: null,
    agencyCommission: null,
    campaignDuration: null,
    soRemark: null,
    inventories: [],
    suffixInventories: [],
    createdBy: null, // Add this
    createdByName: null, // Add this

    //taxes
    taxType: 0,
    IGST: 0,
    SGST: 0,
    CGST: 0,
    municipalTax: 0,
    others: 0,
    round: 0,
  });

  const subTotal = useMemo(
    () => computeBillableSubTotal(salesOrderData),
    [salesOrderData],
  );

  useEffect(() => {
    const totalPercent =
      (Number(salesOrderData?.IGST) || 0) +
      (Number(salesOrderData?.SGST) || 0) +
      (Number(salesOrderData?.CGST) || 0) +
      (Number(salesOrderData?.municipalTax) || 0) +
      (Number(salesOrderData?.otherTaxes) || 0) +
      (Number(salesOrderData?.round) || 0) +
      (Number(salesOrderData?.agencyCommission) || 0);
    const grandTotalValue = (subTotal * totalPercent) / 100 + subTotal;
    setGrandTotal(grandTotalValue);
  }, [salesOrderData, subTotal]);

  //useEffects
  useEffect(() => {
    fetchSalesOrderByID();
  }, [id]);

  const getFinancialYearLabel = (startYear) => {
    const yy = String((startYear + 1) % 100).padStart(2, "0");
    return `${startYear}-${yy}`;
  };

  const getCurrentFinancialYearStart = (d = new Date()) => {
    // FY assumed: Apr 1 -> Mar 31
    const month = d.getMonth(); // 0-indexed
    const year = d.getFullYear();
    return month >= 3 ? year : year - 1;
  };

  const getPreviousFinancialYears = () => {
    const currentStart = getCurrentFinancialYearStart();
    const years = [];
    // Generate down to 2024-25 only
    for (let y = currentStart - 1; y >= 2024; y -= 1) {
      years.push(getFinancialYearLabel(y));
    }
    return years;
  };

  //functions
  const fetchSalesOrderByID = async () => {
    try {
      if (id) {
        let response = await fetch(
          `${apiBaseUrl}/inventoryManagement/getSalesOrderByID?id=${id}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const responseData = await response.json();

        if (response.ok) {
          const salesInfo = responseData.salesInfo;
          const bdfId = salesInfo?.bdfId || {};
          const brandId = bdfId?.brandId || {};
          const partyCompany = brandId?.partyCompany || {};
          const secondLastLog = responseData.secondLastLog || null;

          const apiPrevFlag = Boolean(salesInfo?.isPreviosYear);
          const apiPrevYear = salesInfo?.previousYear || null;
          // Default is disabled unless backend says otherwise
          setIsPreviosYear(apiPrevFlag);
          setPreviousYear(
            apiPrevFlag
              ? apiPrevYear || getPreviousFinancialYears()[0] || null
              : null,
          );

          setSalesOrderData({
            SOConfirmed: salesInfo?.SOConfirmed,
            salesOrderId: salesInfo?._id,
            soRemark: salesInfo?.soRemark || null,
            bdfId: bdfId?._id,
            salesPerson: bdfId?.salesPerson,
            bdfStamp: bdfId?.stamp,
            subBdfDate: bdfId?.bdfDate,
            gstNumber:
              bdfId?.parentCompanyId?.gstNumber ||
              partyCompany?.gstNumber ||
              null,
            stamp: salesInfo.stamp || null,
            roNumber: salesInfo.roNumber || null,
            parentCompanyName: bdfId?.parentCompanyId?.name || bdfId?.parentCompanyName || null,
            remark: bdfId?.remark || null,
            roFile: bdfId?.roFile || null,
            brandName: brandId?.brandName || null,
            motherBrand: brandId?.motherBrand || null,
            brandId: brandId?._id || null,
            brandSegment: bdfId?.brandSegment || null,
            agencyCommission: salesInfo?.agencyCommission || 0,
            campaignDuration: bdfId?.campaignDuration || null,
            campaignName: bdfId?.campaignName || null,
            bdfType: bdfId?.bdfType,
            printingCharge: salesInfo?.printingCharge || 0,
            mountingCharge: salesInfo?.mountingCharge || 0,
            createdBy: bdfId?.createdBy?._id || null, // Add this
            createdByName: bdfId?.createdBy?.name || null,
            inventories:
              bdfId?.inventories?.map((inv) => ({
                _id: inv?.invId?._id || inv?._id || false,
                locationName: inv?.invId?.locations?.name || null,
                CodeNo: inv?.invId?.CodeNo || null,
                suffix: inv?.lineType ? null : inv?.invId?.suffix || "HO",
                maintenanceRate: inv?.maintenanceRate || null,
                isTrading: inv?.invId?.isTrading || false,
                lineType: inv?.lineType || null,
                commissionForId: inv?.commissionForId?._id || null,
                ownedBy: inv?.invId?.ownedBy || inv?.commissionForId?.name || false,
                ownedByGst: inv?.commissionForId?.gstNumber || null,
                rpm: inv?.rpm || 0,
                printingCharge: inv?.printingCharge || 0,
                mountingCharge: inv?.mountingCharge || 0,
                innovationRate: inv?.innovationRate || 0,
                specialRate: inv?.specialRate || 0,
                specialRatePerMonth: inv?.specialRatePerMonth || 0,
                startDate: inv?.startDate || null,
                endDate: inv?.endDate || null,
                width: inv?.invId?.width || null,
                height: inv?.invId?.height || null,
              })) || [],
            suffixInventories:
              bdfId?.suffixInventories?.map((inv) => ({
                _id: inv?._id,
                specialRate: inv?.specialRate || 0,
                purchaseRate: inv?.purchaseRate || 0,
                suffixCode: inv?.suffixCode || null,
                suffixInfo: inv?.suffixInfo || null,
                vendor: inv?.vendor || null,
                startDate: inv?.startDate || null,
                endDate: inv?.endDate || null,
              })) || [],
            //taxes
            taxType: salesInfo?.taxType || 0,
            IGST: salesInfo?.IGST || 0,
            SGST: salesInfo?.SGST || 0,
            CGST: salesInfo?.CGST || 0,
            municipalTax: salesInfo?.municipalTax || 0,
            otherTaxes: salesInfo?.otherTaxes || 0,
            round: salesInfo?.round || 0,
            isFromSub: Boolean(salesInfo?.isFromSub),
            soCreatedAt: salesInfo?.soCreatedAt || null,
            soCreatedByName: salesInfo?.soCreatedBy?.name || null,
            soUpdatedByName: salesInfo?.soUpdatedBy?.name || null,
          });

          if (secondLastLog) {
            setLog({
              parentCompanyName: secondLastLog?.parentCompanyId?.name || secondLastLog?.parentCompanyName || null,
              gstNumber:
                secondLastLog?.parentCompanyId?.gstNumber ||
                secondLastLog?.brandId?.partyCompany?.gstNumber ||
                null,
              remark: secondLastLog?.remark || null,
              brandName: secondLastLog?.brandId?.brandName || null,
              motherBrand: secondLastLog?.brandId?.motherBrand || null,
              brandSegment: secondLastLog?.brandSegment || null,
              campaignDuration: secondLastLog?.campaignDuration || null,
              campaignName: secondLastLog?.campaignName || null,
              bdfType: secondLastLog?.bdfType || null,
              inventories:
                secondLastLog?.inventories?.map((inv) => ({
                  _id: inv?.invId?._id || inv?._id || false,
                  locationName: inv?.invId?.locations?.name || null,
                  CodeNo: inv?.invId?.CodeNo || null,
                  suffix: inv?.lineType ? null : inv?.invId?.suffix || "HO",
                  maintenanceRate: inv?.maintenanceRate || null,
                  isTrading: inv?.invId?.isTrading || false,
                  lineType: inv?.lineType || null,
                  ownedBy: inv?.invId?.ownedBy || inv?.commissionForId?.name || false,
                  rpm: inv?.rpm || 0,
                  printingCharge: inv?.printingCharge || 0,
                  mountingCharge: inv?.mountingCharge || 0,
                  innovationRate: inv?.innovationRate || 0,
                  specialRate: inv?.specialRate || 0,
                  specialRatePerMonth: inv?.specialRatePerMonth || 0,
                  startDate: inv?.startDate || null,
                  endDate: inv?.endDate || null,
                })) || [],
              suffixInventories:
                secondLastLog?.inventories?.map((inv) => ({
                  _id: inv?._id,
                  specialRate: inv?.specialRate || 0,
                  purchaseRate: inv?.purchaseRate || 0,
                  suffixCode: inv?.suffixCode || null,
                  suffixInfo: inv?.suffixInfo || null,
                  vendor: inv?.vendor || null,
                  startDate: inv?.startDate || null,
                  endDate: inv?.endDate || null,
                })) || [],
            });
          }

          return responseData;
        } else {
          notification.error({
            message: responseData?.message || "Failed to fetch the sales order",
          });
        }
      }
    } catch (error) {
      notification.error({
        message: error.message,
      });
    }
  };

  const updateBDFInfoFromSO = async ({ brandName, motherBrand } = {}) => {
    const salesOrderId = salesOrderData?.salesOrderId;
    const brandId = salesOrderData?.brandId;
    if (!salesOrderId || !brandId) {
      message.error("Missing sales order or brand information");
      return false;
    }

    const resolvedBrandName = (brandName ?? salesOrderData?.brandName)?.trim();
    if (!resolvedBrandName) {
      message.error("Brand name is required");
      return false;
    }

    const payload = {
      salesOrderId,
      brandId,
      brandName: resolvedBrandName.toUpperCase(),
    };
    if (motherBrand !== undefined) {
      payload.motherBrand = String(motherBrand).trim().toUpperCase();
    }

    try {
      const token = window.localStorage.getItem("token");
      const res = await fetch(
        `${apiBaseUrl}/inventoryManagement/updateBDFInfoFromSO`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { authorization: `Token ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json();
      if (!res.ok || data?.success === false) {
        message.error(data?.message || "Failed to update brand information");
        return false;
      }
      message.success(data?.message || "Updated successfully");
      setSalesOrderData((prev) => ({
        ...prev,
        ...(brandName !== undefined && {
          brandName: resolvedBrandName.toUpperCase(),
        }),
        ...(motherBrand !== undefined && {
          motherBrand: String(motherBrand).trim().toUpperCase(),
        }),
      }));
      await fetchSalesOrderByID();
      return true;
    } catch (err) {
      message.error(err?.message || "Failed to update brand information");
      return false;
    }
  };

  const updateBrandNameFromSO = (brandName) =>
    updateBDFInfoFromSO({ brandName });

  const updateMotherBrandFromSO = (motherBrand) =>
    updateBDFInfoFromSO({ motherBrand });

  const updateOwnedByOfTradingInv = async (invId, ownedBy) => {
    try {
      if (!invId || !ownedBy) return false;
      const token = window.localStorage.getItem("token");
      const res = await fetch(
        `${apiBaseUrl}/inventoryManagement/updateOwnedByOfTradingInv`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { authorization: `Token ${token}` } : {}),
          },
          body: JSON.stringify({ invId, ownedBy }),
        },
      );
      const j = await res.json();
      if (!res.ok || !j?.success) {
        notification.error({
          message: j?.message || "Failed to update Owned By",
        });
        return false;
      }
      notification.success({
        message: j?.message || "Owned By updated successfully",
      });
      await fetchSalesOrderByID();
      return true;
    } catch (e) {
      notification.error({ message: e?.message || "Failed to update Owned By" });
      return false;
    }
  };

  const handleFormChange = (fieldName, value) => {
    if (fieldName == "taxType") {
      setSalesOrderData((prevData) => ({
        ...prevData,
        [fieldName]: value,
        SGST: value.toFixed(2) / 2,
        CGST: value.toFixed(2) / 2,
        IGST: 0,
      }));
      return;
    }
    if (fieldName == "agencyCommission" || fieldName == "campaignDuration") {
      value = String(value).replace(/\D/g, "");
      setSalesOrderData((prevData) => ({
        ...prevData,
        [fieldName]: Number(value),
      }));
      return;
    }
    if (fieldName == "printingCharge") {
      value = String(value).replace(/\D/g, "");
      setSalesOrderData((prevData) => ({
        ...prevData,
        [fieldName]: Number(value),
      }));
      return;
    }

    if (fieldName == "mountingCharge") {
      value = String(value).replace(/\D/g, "");
      setSalesOrderData((prevData) => ({
        ...prevData,
        [fieldName]: Number(value),
      }));
      return;
    }
    if (fieldName == "IGST") {
      setSalesOrderData((prevData) => ({
        ...prevData,
        [fieldName]: value,
        SGST: 0,
        CGST: 0,
        taxType: 0,
      }));
      return;
    }
    if (
      fieldName == "municipalTax" ||
      fieldName == "round" ||
      fieldName == "otherTaxes"
    ) {
      value = String(value).replace(/\D/g, "");
      setSalesOrderData((prevData) => ({
        ...prevData,
        [fieldName]: Number(value),
      }));
      return;
    }

    setSalesOrderData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const handleInvChange = (value, id, field) => {
    let inv = salesOrderData.inventories;
    const index = inv.findIndex((item) => item._id === id);
    const updatedInventories = [...inv];
    value = value.replace(/\D/g, "");
    updatedInventories[index] = {
      ...updatedInventories[index],
      [field]: Number(value),
    };
    setSalesOrderData((prev) => {
      return {
        ...prev,
        inventories: updatedInventories,
      };
    });
  };

  const openRoFileInNewTab = (url) => {
    if (!url) {
      message.error("RO file not found");
      return;
    }
    openFileInNewTab(url);
  };

  const handleSalesOrderSubmission = async (isSaveOnly = false) => {
    // Prevent submission if the respective button is already processing
    if ((isSaveOnly && isSaving) || (!isSaveOnly && isConfirming)) return;

    if (isSaveOnly) {
      setIsSaving(true);
    } else {
      setIsConfirming(true);
    }
    //validation
    let errorFlag = false;
    salesOrderData?.inventories.map((ele) => {
      if (!ele.startDate || !ele.endDate) {
        errorFlag = true;
        notification.error({ message: "Dates cant be left empty" });
      }
    });
    if (errorFlag) {
      if (isSaveOnly) {
        setIsSaving(false);
      } else {
        setIsConfirming(false);
      }
      return;
    }

    // alert("Submitted amigos")
    //submission
    try {
      const normalizedPrevFlag = Boolean(isPreviosYear);
      const normalizedPrevYear = normalizedPrevFlag
        ? previousYear || getPreviousFinancialYears()[0] || null
        : null;

      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/editSalesOrder`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...salesOrderData,
            isSaveOnly,
            isPreviosYear: normalizedPrevFlag,
            previousYear: normalizedPrevYear,
          }),
        },
      );

      const responseData = await response.json();
      if (responseData?.success) {
        notification.success({ message: responseData?.message });
        const qs = window.location.search || "";
        navigate(`/sales-order${qs}`);
      } else {
        notification.error({ message: responseData?.message });
      }
    } catch (err) {
      console.error("Error:", err.message);
    } finally {
      if (isSaveOnly) {
        setIsSaving(false);
      } else {
        setIsConfirming(false);
      }
    }
  };
  console.log("Upload RO", fileUrl);
  const handleROModal = () => {
    setROModal(true);
  };

  const handleUpload = async ({ file, onSuccess, onError }) => {
    try {
      // Step 1: Get the pre-signed URL from the backend
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/uploadPolicy`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            mime: file.type,
            fileName: file.name,
          }),
        },
      );

      const { data: policyData, filePath } = await response.json();
      const uploadUrl = convertS3ToCloudFront(`${policyData.url}/${filePath}`);

      // Step 2: Use the URL to upload directly to S3
      const formData = new FormData();

      Object.entries(policyData.fields).forEach(([key, value]) => {
        // Only send Content-Type if the presigned POST policy includes it.
        if (key.toLowerCase() === "content-type") {
          formData.append(key, file.type || "application/octet-stream");
          return;
        }
        formData.append(key, value);
      });

      formData.append("file", file);

      const s3Response = await fetch(policyData.url, {
        method: "POST",
        body: formData,
      });

      if (s3Response.ok) {
        onSuccess("Ok");
        message.success(`${file.name} uploaded successfully`);
        setFileUrl(uploadUrl); // Store the file URL

        setSalesOrderData((prevData) => ({
          ...prevData,
          roFile: uploadUrl,
        }));

        setROModal(false);
      } else {
        throw new Error("S3 upload failed");
      }
    } catch (error) {
      console.error(error);
      onError(error);
      message.error(`Failed to upload ${file.name}`);
    }
  };
  const handleGstChange = (value) => {
    setSwitchGst(value);
    console.log("GST Toggle:", value);
  };

  const handleExportExcel = async () => {
    setIsExportingExcel(true);
    try {
      await exportSalesOrderDetailsExcel({
        salesOrderData,
        log,
        subTotal,
        grandTotal,
      });
      message.success("Excel exported successfully");
    } catch (err) {
      console.error(err);
      message.error("Failed to export Excel");
    } finally {
      setIsExportingExcel(false);
    }
  };

  // console.log("Sales Order Data:", salesOrderData);
  return (
    <div style={{ marginTop: -40 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <div style={{ display: "flex", justifyContent: "end", gap: "20px" }}>
          {salesOrderData?.bdfType === "F" && (
            <Tag
              color="green"
              style={{
                fontSize: "16px",
                padding: "5px 12px",
                fontWeight: "bold",
              }}
            >
              FOC
            </Tag>
          )}

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "12px",
              flexWrap: "nowrap",
            }}
          >
            <Button
              type={salesOrderData?.roFile ? "default" : "primary"}
              icon={
                salesOrderData?.roFile ? (
                  <AiOutlineEye />
                ) : (
                  <AiOutlineUpload />
                )
              }
              style={{
                borderColor: salesOrderData?.roFile ? "#52c41a" : undefined,
                color: salesOrderData?.roFile ? "#52c41a" : undefined,
              }}
              onClick={handleROModal}
            >
              {salesOrderData?.roFile ? "RO File Available • View" : "Upload RO"}
            </Button>
            <Button
              type="primary"
              icon={<FaFileExcel />}
              loading={isExportingExcel}
              onClick={handleExportExcel}
              disabled={!salesOrderData?.salesOrderId}
            >
              Export Excel
            </Button>
          </div>

          <Modal
            title="RO"
            open={ROModal}
            onCancel={() => setROModal(false)}
            footer={null}
            width={600}
          >
            <div
              style={{ width: "100%", display: "grid", placeItems: "center" }}
            >
              {!salesOrderData?.roFile ? (
                <Dragger customRequest={handleUpload} style={{ width: "100%" }}>
                  <p className="ant-upload-text">
                    Click or drag file to this area to upload
                  </p>
                  <p className="ant-upload-hint">Supports single file upload</p>
                </Dragger>
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <Button
                    type="default"
                    icon={<AiOutlineEye style={{ marginRight: 2 }} />}
                    style={{
                      margin: "1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                    onClick={() => {
                      openRoFileInNewTab(salesOrderData?.roFile);
                      setROModal(false);
                    }}
                  >
                    View File
                  </Button>

                  <Upload customRequest={handleUpload} showUploadList={false}>
                    <Button
                      type="default"
                      icon={<AiOutlineUpload style={{ marginRight: 2 }} />}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      Update RO
                    </Button>
                  </Upload>
                </div>
              )}
            </div>
          </Modal>
        </div>
      </div>
      <SalesOrderForm
        salesOrderData={salesOrderData}
        handleFormChange={handleFormChange}
        log={log}
        onSaveBrandName={updateBrandNameFromSO}
        onSaveMotherBrand={updateMotherBrandFromSO}
      />
      <div style={{ marginBottom: "3rem" }}>
        {salesOrderData?.inventories &&
          salesOrderData?.inventories?.length > 0 &&
          (!salesOrderData?.isFromSub ||
            (salesOrderData?.isFromSub && salesOrderData?.subBdfDate)) && (
            <InventoryList
              salesOrderData={salesOrderData}
              handleInvChange={handleInvChange}
              setSalesOrderData={setSalesOrderData}
              handleFormChange={handleFormChange}
              log={log}
              updateOwnedByOfTradingInv={updateOwnedByOfTradingInv}
              isSubBdf={
                salesOrderData?.isFromSub && !!salesOrderData?.subBdfDate
              }
            />
          )}
      </div>
      {salesOrderData?.suffixInventories &&
        salesOrderData?.suffixInventories?.length > 0 &&
        !salesOrderData?.isFromSub && (
          <SuffixInvList
            salesOrderData={salesOrderData}
            setSalesOrderData={setSalesOrderData}
          />
        )}

      {salesOrderData?.isFromSub && salesOrderData?.subBdfDate && (
        <div
          style={{
            marginBottom: "1.5rem",
            padding: "12px 16px",
            backgroundColor: "#f0f7ff",
            borderRadius: "6px",
            border: "1px solid #d0e3ff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <span style={{ fontWeight: "600", marginRight: "8px" }}>
              Production Date:
            </span>
            <span>{salesOrderData?.subBdfDate}</span>
          </div>
          <Tag color="blue" style={{ fontSize: "14px", padding: "2px 10px" }}>
            Sub BDF
          </Tag>
        </div>
      )}
      <div
        className="grandTotal-TotalProductionCost-wrapper"
        style={{
          marginBottom: justView ? "2rem" : "0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <GrandTotal
          salesOrderData={salesOrderData}
          subTotal={subTotal}
          handleFormChange={handleFormChange}
          grandTotal={grandTotal}
        />
        <TotalProductionCost
          salesOrderData={salesOrderData}
          handleFormChange={handleFormChange}
        />
        {!justView && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
              Make SO in previous year
            </span>
            <Switch
              checked={isPreviosYear}
              onChange={(checked) => {
                setIsPreviosYear(checked);
                if (checked) {
                  setPreviousYear(
                    (prev) => prev || getPreviousFinancialYears()[0] || null,
                  );
                } else {
                  setPreviousYear(null);
                }
              }}
            />
            <Select
              value={previousYear}
              onChange={(v) => setPreviousYear(v)}
              style={{ minWidth: 120 }}
              placeholder="Select FY"
              disabled={!isPreviosYear}
              options={getPreviousFinancialYears().map((fy) => ({
                label: fy,
                value: fy,
              }))}
            />
          </div>
        )}
      </div>
      {!justView && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "0.75rem",
            marginBottom: "2rem",
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
            {!salesOrderData?.SOConfirmed && (
              <Button
                type="primary"
                onClick={() => handleSalesOrderSubmission(true)}
                disabled={isSaving}
              >
                {isSaving ? "Processing..." : "Save"}
              </Button>
            )}

            <Button
              type="primary"
              onClick={() =>
                handleSalesOrderSubmission(Boolean(salesOrderData?.SOConfirmed))
              }
              disabled={salesOrderData?.SOConfirmed ? isSaving : isConfirming}
            >
              {salesOrderData?.SOConfirmed
                ? isSaving
                  ? "Processing..."
                  : "Save"
                : isConfirming
                  ? "Processing..."
                  : "Confirm"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesOrderDetails;
